import i18n from '../i18n';

type MailRecipient = string | string[] | undefined;

export type OpenMailClientOptions = {
  to?: MailRecipient;
  cc?: MailRecipient;
  bcc?: MailRecipient;
  subject?: string;
  body?: string;
};

const MAIL_SIGNATURE_TEXT = [
  'www.missionfund.org',
  '',
  i18n.t('common.mail.signatureKo'),
  i18n.t('common.mail.signatureEn'),
].join('\n');

const normalizeRecipients = (value?: MailRecipient): string | undefined => {
  if (!value) return undefined;
  const list = (Array.isArray(value) ? value : [value])
    .map((item) => item.trim())
    .filter(Boolean);
  return list.length ? list.join(',') : undefined;
};

export const buildMailtoUrl = (options: OpenMailClientOptions): string => {
  const to = normalizeRecipients(options.to);
  const cc = normalizeRecipients(options.cc);
  const bcc = normalizeRecipients(options.bcc);
  const subject = options.subject?.trim().replace(/\+/g, ' ');
  const body = (options.body ?? '').replace(/\+/g, ' ');

  const encodedParams: string[] = [];
  const addParam = (key: string, value?: string) => {
    if (!value) return;
    encodedParams.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
  };

  addParam('cc', cc);
  addParam('bcc', bcc);
  addParam('subject', subject);
  addParam('body', body);

  const query = encodedParams.join('&').replace(/\+/g, '%20');
  return `mailto:${to ?? ''}${query ? `?${query}` : ''}`;
};

const MAILTO_URL_MAX_LENGTH = 2000;

export const openMailClient = (options: OpenMailClientOptions): void => {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  const normalizedSubject = (options.subject ?? '').replace(/\+/g, ' ');
  const baseBody = (options.body ?? '').replace(/\+/g, ' ').trimEnd();
  const bodyWithSignature = baseBody.includes('www.missionfund.org')
    ? baseBody
    : [baseBody, '', MAIL_SIGNATURE_TEXT].filter(Boolean).join('\n');

  let mailtoUrl = buildMailtoUrl({ ...options, subject: normalizedSubject, body: bodyWithSignature });

  if (mailtoUrl.length > MAILTO_URL_MAX_LENGTH && bodyWithSignature) {
    const maxBodyChars = Math.floor((MAILTO_URL_MAX_LENGTH - 300) / 3);
    const truncatedBody =
      bodyWithSignature.slice(0, Math.max(100, maxBodyChars)) +
      `\n\n${i18n.t('common.mail.truncatedHint')}`;
    mailtoUrl = buildMailtoUrl({ ...options, subject: normalizedSubject, body: truncatedBody });
  }

  // Some mail clients display '+' literally, so force-space encoding.
  const normalizedMailtoUrl = mailtoUrl.replace(/\+/g, '%20');
  triggerMailto(normalizedMailtoUrl);
};

const triggerMailto = (url: string): void => {
  const link = document.createElement('a');
  link.href = url;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};
